"use client";

import { useRef, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Datepicker from "react-tailwindcss-datepicker";
import { auditorSchema } from "../Schema/auditorSchema";
import { useCreateAuditor, useCreateDocument } from "../custom-hooks/queries";
import { countries } from "../utils/constants";
import ComboBox from "./ComboBox";
import { ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import { getBlobSasToken } from "../api/api-methods";
import { BlockBlobClient } from "@azure/storage-blob";
import { v4 as uuidv4 } from "uuid";
import Notification from "./Notification";

export default function FormModal({ open, setOpen }) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(auditorSchema),
  });
  const { mutate } = useCreateAuditor();

  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("success");
  const { mutate: createDocumentMutation, data: createDocData } =
    useCreateDocument();
  const newGuid = uuidv4();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
    } else {
      setFileName("");
    }
  };
  const onFileChange = (event) => {
    const files = event.target.files;
    const file = event.target.files[0];

    if (file) {
      setFileName(file.name);
    } else {
      setFileName("");
    }
    if (files.length > 0) {
      setValue("cv", files, { shouldValidate: true });
    }
  };
  const onSubmit = async (data) => {
    const file = data.cv;

    // if (!selectedFile) return;

    try {
      const blobData = await getBlobSasToken("documents", newGuid);
      const { storage_account_name, container_name, sas_token } = blobData;

      const blobUrl = `https://${storage_account_name}.blob.core.windows.net/${container_name}/${file[0].name}?${sas_token}`;

      const blobClient = new BlockBlobClient(blobUrl);

      await blobClient.uploadData(file);

      const documentMetadataPayload = {
        document_name: file[0].name,
        document_type: "CV",
        document_version: "1.0",
        document_url: blobUrl.split("?")[0],
        provider_id: null,
        vqa_id: null,
        is_draft: false,
        rfi_id: null,
        document_location: null,
        provider_name: null,
      };

      createDocumentMutation(documentMetadataPayload);

      const auditorData = {
        first_name: data?.firstName,
        last_name: data?.lastName,
        office_phone: data?.officePhone,
        email_address: data.emailAddress,
        country: data.country.name,
        city: data.city,
        state: data.state,
        alternate_email_address: data.secondEmailAddress,
        company_affiliation: data.companyAffiliation,
        mobile_phone: data.mobilePhone,
        training_status: data.training,
        training_completion_date: data.dateOfCompletion.startDate,
        cv_document_id: createDocData?.document_id,
      };
      mutate(
        { auditorData },
        {
          onSuccess: () => {
            setMessage("Updated successfully");
            setStatus("success");
            setShow(true);
          },
        },
        {
          onError: () => {
            setMessage("Error Updating");
            setStatus("error");
            setShow(true);
          },
        }
      );

      //   alert(`File "${selectedFile.name}" uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading file:", error);
      setMessage("Error Updating");
      setStatus("error");
      setShow(true);
    } finally {
      setOpen(false);
    }

    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
            <div>
              <DialogTitle
                as="h3"
                className="text-base font-semibold text-gray-900 text-center"
              >
                Auditor Form
              </DialogTitle>
              <div className="mt-5">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* First Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        {...register("firstName")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="First Name"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.firstName?.message}
                      </p>
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        {...register("lastName")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Last Name"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.lastName?.message}
                      </p>
                    </div>

                    {/* Email Address */}
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email Address
                      </label>
                      <input
                        {...register("emailAddress")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Email Address"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.emailAddress?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        2nd Email Address (Alternate)
                      </label>
                      <input
                        {...register("secondEmailAddress")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Email Address"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.secondEmailAddress?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Date of Completion
                      </label>
                      <Controller
                        name="dateOfCompletion"
                        control={control}
                        render={({ field }) => (
                          <div className="relative w-full">
                            <Datepicker
                              {...field}
                              name="dateOfCompletion"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select Date"
                              useRange={false}
                              displayFormat="MMMM DD, YYYY"
                              asSingle
                              minDate={new Date()}
                              containerClassName="w-full"
                              popperClassName="datepicker-dropdown w-[300px] lg:w-[400px] xl:w-[500px] p-2 border border-gray-300 rounded-lg shadow-lg bg-white grid grid-cols-2 gap-1 h-28 overflow-y-auto"
                              inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                            />
                          </div>
                        )}
                      />
                      <p className="text-red-600 text-xs">
                        {errors.dateOfCompletion?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Company Affiliation
                      </label>
                      <input
                        {...register("companyAffiliation")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder=" Company Affiliation"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.companyAffiliation?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Office Phone
                      </label>
                      <input
                        {...register("officePhone")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Office Phone"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.officePhone?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Mobile Phone{" "}
                      </label>
                      <input
                        {...register("mobilePhone")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Mobile Phone"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.mobilePhone?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Country
                      </label>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <ComboBox
                            {...field}
                            data={countries}
                            placeHolder="Select Country"
                            setSelected={(value) => field.onChange(value)}
                            selected={field.value}
                            width={"w-[300px] lg:w-[400px] xl:w-[200px] "}
                          />
                        )}
                      />

                      <p className="text-red-600 text-xs">
                        {errors.country?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        City
                      </label>
                      <input
                        {...register("city")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="City"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.city?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        State/Province
                      </label>
                      <input
                        {...register("state")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="State/Province"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.state?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Training
                      </label>
                      <input
                        {...register("training")}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Training"
                      />
                      <p className="text-red-600 text-xs">
                        {errors.training?.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Upload CV*
                      </label>
                      <div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current.click()}
                          className="inline-flex items-center justify-center w-12 mt-1 rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          <ArrowUpTrayIcon className="w-5 h-5 " />
                        </button>
                        <input
                          {...register("cv")}
                          type="file"
                          ref={fileInputRef}
                          onChange={onFileChange}
                          accept="application/pdf"
                          className="hidden"
                        />
                        <span className="text-sm text-gray-700 ml-3">
                          <span className="ml-3 text-sm text-gray-700">
                            {fileName}
                          </span>{" "}
                        </span>
                        <p className="text-red-600 text-xs">
                          {errors.cv?.message}
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Buttons */}
                  <div className="mt-6 flex space-x-4">
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Submit
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
      <Notification
        show={show}
        setShow={setShow}
        errorMessage={message}
        status={status}
      />
    </Dialog>
  );
}
